import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Heart, MapPin, ChevronDown } from "lucide-react";
import { CONTACT, NAV_LINKS } from "../data";
import WhatsAppIcon from "./WhatsAppIcon";
import { whatsappLink } from "../whatsapp";
import PromoBar from "./PromoBar";
import useWishlist from "../useWishlist";

export default function Header({ cartCount, onCartOpen }) {
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const wishlist = useWishlist();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenSub(null);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-40"
    >
      <PromoBar />
      <div className={`transition-all duration-500 ${scrolled ? "py-2.5" : "py-4"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="pill h-16 md:h-[68px] px-3 md:px-5 flex items-center justify-between shadow-soft">
            <Link
              to="/"
              data-testid="brand-logo-link"
              className="flex items-center gap-3 group pl-1"
            >
              <span className="grid place-items-center h-10 w-10 rounded-full bg-boneSoft ring-1 ring-ink/10 overflow-hidden p-1.5 transition-transform group-hover:rotate-[8deg] duration-500">
                <img
                  src={CONTACT.logo}
                  alt="Organo Mushroom"
                  className="h-full w-full object-contain"
                />
              </span>
              <span
                className="text-[22px] md:text-[26px] leading-none font-bold tracking-tight"
                style={{ fontFamily: "'Playfair Display', Georgia, serif", color: "#1E3A8A" }}
              >
                Organo Mushroom
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-6 absolute left-1/2 -translate-x-1/2">
              {NAV_LINKS.map((n) => (
                <DesktopNavItem key={n.to} item={n} />
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <Link
                to="/track"
                data-testid="track-order-link"
                className="hidden lg:inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.22em] font-medium text-ink/65 hover:text-coral px-3 transition-colors"
              >
                <MapPin className="h-3.5 w-3.5" />
                Track order
              </Link>
              <a
                data-testid="header-whatsapp"
                href={whatsappLink("Hi Organo Mushroom 👋\n\nI have a quick question about your products.")}
                target="_blank"
                rel="noreferrer"
                aria-label="Chat on WhatsApp"
                className="hidden sm:grid h-10 w-10 place-items-center rounded-full border border-ink/10 text-whatsapp hover:bg-whatsapp hover:text-white hover:border-whatsapp transition-all duration-300 hover:scale-105"
              >
                <WhatsAppIcon className="h-4 w-4" />
              </a>
              <Link
                to="/wishlist"
                data-testid="wishlist-link"
                aria-label="View wishlist"
                className="relative h-10 w-10 grid place-items-center rounded-full border border-ink/10 hover:border-coral hover:bg-coral hover:text-white transition-all duration-300 hover:scale-105"
              >
                <Heart className="h-4 w-4" />
                {wishlist.count > 0 && (
                  <span
                    data-testid="wishlist-count-badge"
                    className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-coral text-white text-[11px] font-medium grid place-items-center ring-2 ring-bone"
                  >
                    {wishlist.count}
                  </span>
                )}
              </Link>
              <button
                data-testid="cart-drawer-toggle"
                onClick={onCartOpen}
                aria-label="Open cart"
                className="relative h-10 w-10 grid place-items-center rounded-full border border-ink/10 hover:border-coral hover:bg-coral hover:text-white transition-all duration-300 hover:scale-105"
              >
                <ShoppingBag className="h-4 w-4" />
                {cartCount > 0 && (
                  <span
                    data-testid="cart-count-badge"
                    className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-coral text-white text-[11px] font-medium grid place-items-center ring-2 ring-bone"
                  >
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setOpen((o) => !o)}
                aria-label="Toggle menu"
                className="md:hidden h-10 w-10 grid place-items-center rounded-full border border-ink/10"
              >
                {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {open && (
            <div
              data-testid="mobile-menu"
              className="md:hidden mt-2 rounded-3xl bg-bone/95 backdrop-blur-xl border border-ink/10 overflow-hidden shadow-soft max-h-[70vh] overflow-y-auto"
            >
              <nav className="flex flex-col px-5 py-2">
                {[...NAV_LINKS, { label: "Track order", to: "/track" }].map((n, idx) => (
                  <MobileNavItem
                    key={n.to + n.label}
                    item={n}
                    idx={idx}
                    expanded={openSub === n.label}
                    onToggle={() =>
                      setOpenSub((curr) => (curr === n.label ? null : n.label))
                    }
                  />
                ))}
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function DesktopNavItem({ item }) {
  const hasChildren = item.children && item.children.length > 0;
  const [hover, setHover] = useState(false);

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `link-underline text-[13px] font-medium transition-colors ${
            isActive ? "text-coral is-active" : "text-ink/75 hover:text-coral"
          }`
        }
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative flex items-center"
    >
      <NavLink
        to={item.to}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className={({ isActive }) =>
          `link-underline text-[13px] font-medium transition-colors whitespace-nowrap ${
            isActive ? "text-coral is-active" : "text-ink/75 hover:text-coral"
          }`
        }
      >
        {item.label}
      </NavLink>
      <ChevronDown
        className={`ml-1 h-3 w-3 text-ink/55 transition-transform duration-300 ${hover ? "rotate-180 text-coral" : ""}`}
        strokeWidth={2}
      />

      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-all duration-300 ${
          hover
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="min-w-[220px] rounded-2xl bg-boneSoft border border-ink/10 shadow-hoverCard p-2">
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`nav-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="block px-4 py-3 rounded-xl text-[13.5px] text-ink/85 hover:bg-bone hover:text-coral transition-colors"
            >
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileNavItem({ item, idx, expanded, onToggle }) {
  const hasChildren = item.children && item.children.length > 0;
  const border = idx > 0 ? "border-t border-ink/5" : "";

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          `py-3 text-base flex items-center gap-3 ${border} ${
            isActive ? "text-coral" : "text-ink/85"
          }`
        }
      >
        <span className="text-coral/70 text-[11px] tracking-[0.22em] tabular-nums">
          {String(idx + 1).padStart(2, "0")}
        </span>
        {item.label}
      </NavLink>
    );
  }

  return (
    <div className={`py-1 ${border}`}>
      <div className="flex items-center">
        <NavLink
          to={item.to}
          className={({ isActive }) =>
            `flex-1 py-3 text-base flex items-center gap-3 ${
              isActive ? "text-coral" : "text-ink/85"
            }`
          }
        >
          <span className="text-coral/70 text-[11px] tracking-[0.22em] tabular-nums">
            {String(idx + 1).padStart(2, "0")}
          </span>
          {item.label}
        </NavLink>
        <button
          onClick={onToggle}
          aria-label={expanded ? "Collapse" : "Expand"}
          className={`h-9 w-9 grid place-items-center rounded-full hover:bg-ink/5 transition-transform ${expanded ? "rotate-180" : ""}`}
        >
          <ChevronDown className="h-4 w-4 text-muted" />
        </button>
      </div>
      {expanded && (
        <ul className="pb-2 pl-9 space-y-1">
          {item.children.map((c) => (
            <li key={c.to}>
              <Link
                to={c.to}
                className="block py-2 text-sm text-ink/70 hover:text-coral"
              >
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
